import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const UserVerification = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'verify', 'reject', 'request-docs'
  const [rejectionReason, setRejectionReason] = useState('');
  const [documentsNeeded, setDocumentsNeeded] = useState([]);

  useEffect(() => {
    fetchPendingVerifications();
    // eslint-disable-next-line
  }, [filter]);

  const fetchPendingVerifications = async () => {
    try {
      setLoading(true);
      const params = {
        role: filter !== 'all' ? filter : undefined
      };
      const response = await api.get('/admin/verifications/pending', { params });
      setPendingUsers(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to fetch pending verifications';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleVerify = async (userId) => {
    try {
      await api.patch(`/admin/users/${userId}/verify`);
      toast.success('User verified successfully');
      fetchPendingVerifications();
    } catch (error) {
      console.error('Error verifying user:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to verify user';
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await api.patch(`/admin/users/${selectedUser._id}/reject`, {
        reason: rejectionReason
      });
      toast.success('User verification rejected');
      setShowModal(false);
      setRejectionReason('');
      fetchPendingVerifications();
    } catch (error) {
      console.error('Error rejecting user:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to reject user';
      toast.error(errorMessage);
    }
  };

  const handleRequestDocuments = async () => {
    if (documentsNeeded.length === 0) {
      toast.error('Please select at least one document type');
      return;
    }

    try {
      await api.patch(`/admin/users/${selectedUser._id}/request-documents`, {
        documents: documentsNeeded
      });
      toast.success('Document request sent to user');
      setShowModal(false);
      setDocumentsNeeded([]);
      fetchPendingVerifications();
    } catch (error) {
      console.error('Error requesting documents:', error);
      const errorMessage =
        error.error?.message ||
        error.message ||
        'Failed to request documents';
      toast.error(errorMessage);
    }
  };

  const openModal = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setShowModal(true);
  };

  const toggleDocument = (doc) => {
    setDocumentsNeeded(prev =>
      prev.includes(doc)
        ? prev.filter(d => d !== doc)
        : [...prev, doc]
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading pending verifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>User Verification</h1>
          <p>Review and verify employer & consultant accounts</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <div className="info-icon">
          <span class="material-symbols-outlined">
            privacy_tip
            </span>
          </div>
        <div>
          <h3>Verification Policy</h3>
          <ul>
            <li><strong>Candidates:</strong> Auto-verified after OTP (no admin approval needed)</li>
            <li><strong>Employers:</strong> Require admin verification before posting jobs</li>
            <li><strong>Consultants:</strong> Require admin verification before accessing platform</li>
          </ul>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filters-section">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Pending ({pendingUsers.length})
          </button>
          <button
            className={`filter-tab ${filter === 'employer' ? 'active' : ''}`}
            onClick={() => setFilter('employer')}
          >
            Employers
          </button>
          <button
            className={`filter-tab ${filter === 'consultant' ? 'active' : ''}`}
            onClick={() => setFilter('consultant')}
          >
            Consultants
          </button>
        </div>
      </div>

      {/* Pending Users Table */}
      <div className="table-container mb-4">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Company/Agency</th>
              <th>Created By</th>
              <th>Registered</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  🎉 No pending verifications
                </td>
              </tr>
            ) : (
              pendingUsers.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`${user.role === 'employer' ? 'badge badge-primary' : 'badge badge-secondary'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>{user.companyWebsite || user.company?.name || 'N/A'}</td>
                  <td>
                    <span className={` ${user.createdBy === 'admin' ? 'badge badge-warning' : 'badge badge-info'}`}>
                      {user.createdBy === 'admin' ? 'Admin' : 'Self'}
                    </span>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleVerify(user._id)}
                        title="Approve & Verify"
                      >
                        Verify
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => openModal(user, 'request-docs')}
                        title="Request Documents"
                      >
                        Docs
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => openModal(user, 'reject')}
                        title="Reject"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Action Modal */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{borderBottom:'none', marginBottom:'0px'}}>
              <h2>
                {actionType === 'reject' && 'Reject Verification'}
                {actionType === 'request-docs' && 'Request Documents'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body application_detail">
              <div className="detail-section">
                <h3>User Information</h3>
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Role:</strong> {selectedUser.role}</p>
              </div>

              {actionType === 'reject' && (
                <div className="detail-section">
                  <h3>Rejection Reason</h3>
                  <textarea
                    className="form-textarea"
                    rows="5"
                    placeholder="Enter reason for rejection..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                  <button
                    className="btn btn-danger btn-sm mt-2 mb-1"
                    style={{marginLeft:'auto', display:'block'}}
                    onClick={handleReject}
                  >
                    Confirm Rejection
                  </button>
                </div>
              )}

              {actionType === 'request-docs' && (
                <div className="detail-section">
                  <h3>Required Documents</h3>
                  <p className="text-muted">Select documents to request from the user:</p>
                  <div className="document-checkboxes">
                    {['GST Certificate', 'PAN Card', 'Business License', 'Company Registration', 'Other'].map(doc => (
                      <label key={doc} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={documentsNeeded.includes(doc)}
                          onChange={() => toggleDocument(doc)}
                        />
                        <span>{doc}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    className="btn btn-warning btn-sm mb-1"
                    style={{marginLeft:'auto', display:'block'}}
                    onClick={handleRequestDocuments}
                  >
                    Send Document Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserVerification;
